// Backend IDs stay authoritative even when display names or response timing change.
const procedureTypeById: Record<string, string> = {
  proc911: 'emergency',
  proc712: 'exam-regular-patient',
  proc711: 'exam-new-patient',
  proc813: 'dental-cleaning-DH-60',
  proc914: 'cons-aligner',
  proc919: 'cons-impl',
  proc915: 'cons-bleaching',
  proc916: 'cons-splint',
  proc917: 'cons-aesth',
  proc918: 'cons-8',
  proc713: 'exam-child',
  proc935: 'second-opinion',
};

export const getProcedureType = (procedureId: string, procedureName?: string) => {
  const type = procedureTypeById[procedureId] ?? String(procedureName ?? '').trim();
  if (type === 'cons-spint') return 'cons-splint';
  if (type.startsWith('dental-cleaning-')) return 'dental-cleaning-DH-60';
  return type;
};

const regularAppearance = { className: 'bg-[#fbf8f1] text-[#c5b18d]', color: '#c5b18d' };
const emergencyAppearance = { className: 'bg-[#fff1ef] text-[#e06c63]', color: '#e06c63' };

export const getProcedureIconAppearance = (procedureId: string, procedureName?: string) =>
  getProcedureType(procedureId, procedureName) === 'emergency' ? emergencyAppearance : regularAppearance;

const toothOutline = 'M12 5C10.2 3.1 6.2 2.8 4.8 5.8c-1.1 2.5.2 5.3 1 7.4.6 1.7.8 7.8 2.8 7.8 1.5 0 1.2-5.5 3.4-5.5s1.9 5.5 3.4 5.5c2 0 2.2-6.1 2.8-7.8.8-2.1 2.1-4.9 1-7.4-1.4-3-5.4-2.7-7.2-.8Z';

export const ProcedureIcon = ({ procedureId, procedureName }: { procedureId: string; procedureName?: string }) => {
  const type = getProcedureType(procedureId, procedureName);
  let content: JSX.Element;

  switch (type) {
    case 'emergency':
      content = (
        <path d="M10 2h4v6.5l5.6-3.2 2 3.4L16 12l5.6 3.3-2 3.4-5.6-3.2V22h-4v-6.5l-5.6 3.2-2-3.4L8 12 2.4 8.7l2-3.4L10 8.5Z" fill="currentColor" stroke="none" />
      );
      break;
    case 'exam-regular-patient':
    case 'exam-new-patient':
      content = (
        <>
          <rect x="3" y="3" width="18" height="18" rx="2" fill="currentColor" stroke="none" />
          <path d="M6.5 7h4M6.5 11h3M6.5 15h3m3-4 2 2 3.5-4" stroke="#fff" />
        </>
      );
      break;
    case 'dental-cleaning-DH-60':
      content = (
        <>
          <path d={toothOutline} />
          <path d="m3 1 .6 2.4L6 4l-2.4.6L3 7l-.6-2.4L0 4l2.4-.6ZM20 1l.4 1.6L22 3l-1.6.4L20 5l-.4-1.6L18 3l1.6-.4Z" fill="currentColor" stroke="none" />
        </>
      );
      break;
    case 'cons-aligner':
      content = (
        <g strokeWidth=".9">
          {[0, 10].map((offset) => (
            <path key={offset} transform={`translate(${offset} 0)`} d="M7 6.2C5.7 5.1 3.7 5.5 3 7c-1.1 2.1.6 5.6 1.2 10.3.2 1.5 1.2 1.7 1.7.2L7 13.9l1.1 3.6c.5 1.5 1.5 1.3 1.7-.2.6-4.7 2.3-8.2 1.2-10.3-.7-1.5-2.7-1.9-4-.8Z" />
          ))}
          <rect x="5.3" y="10" width="3.4" height="3.4" rx=".6" fill="#fbf8f1" />
          <rect x="15.3" y="10" width="3.4" height="3.4" rx=".6" fill="#fbf8f1" />
          <path d="M1 11.7h22" />
        </g>
      );
      break;
    case 'cons-impl':
      content = (
        <>
          <path d="M12 4C10 2 6 3 6 6.5c0 3 1.5 5.5 3.5 5.5h5C16.5 12 18 9.5 18 6.5 18 3 14 2 12 4Z" />
          <path d="m9 12 1 9a2 2 0 0 0 4 0l1-9M9.4 15h5.2m-4.9 3h4.6m-4.2 3h3.8" />
        </>
      );
      break;
    case 'cons-bleaching':
      content = (
        <>
          <path d="m3 19 11-11 3 3L6 22Z" fill="currentColor" stroke="none" />
          <path d="m12 10 3 3" stroke="#fbf8f1" />
          <path d="m7 2 .6 2.4L10 5l-2.4.6L7 8l-.6-2.4L4 5l2.4-.6Zm12-1 .6 2.4L22 4l-2.4.6L19 7l-.6-2.4L16 4l2.4-.6Zm1 12 .6 2.4L23 16l-2.4.6L20 19l-.6-2.4L17 16l2.4-.6Z" fill="currentColor" stroke="none" />
        </>
      );
      break;
    case 'cons-splint':
      content = (
        <path d="M2 16v-6c0-3.7 5-3.7 5 0 0-4.3 5-4.3 5 0 0-4.3 5-4.3 5 0 0-3.7 5-3.7 5 0v6q0 1-1 1h-3q-1 0-1-1 0 1-1 1h-3q-1 0-1-1 0 1-1 1H8q-1 0-1-1 0 1-1 1H3q-1 0-1-1Z" strokeWidth="1.2" />
      );
      break;
    case 'cons-aesth':
      content = (
        <>
          <path d={toothOutline} />
          <path d="M1 9h1m20 0h1M2 15l1-.5m18 .5-1-.5M5 1l1 1m12-1-1 1m-7.5 9.5q2.5 2.5 5 0" />
          <circle cx="9" cy="9" r=".7" fill="currentColor" stroke="none" />
          <circle cx="15" cy="9" r=".7" fill="currentColor" stroke="none" />
        </>
      );
      break;
    case 'cons-8':
      content = (
        <>
          <path d="M16 5c-2-2-5-1-5 1.8 0 1.4.6 2.7 1 4.2.3 1.4.7 6 2 6 1 0 .7-4 2-4s1 4 2 4c1.3 0 1.7-4.6 2-6 .4-1.5 1-2.8 1-4.2C21 4 18 3 16 5Z" />
          <path d="M8 11c-1.4-2.4-4.5-2-5 .5-.3 1.5 0 2.8 0 4.3 0 1.4-.7 5.8.6 6.1 1 .2 1.6-3.5 2.8-3.2s.2 4 1.2 4.2c1.3.3 2.6-4 3.2-5.3.7-1.3 1.6-2.4 1.7-3.9.2-2.5-2.6-3.7-4.5-2.7Z" />
          <path d="M1 9q4-2 9 1M7 5V3M3.5 6.5l-1-1" />
        </>
      );
      break;
    case 'exam-child':
      content = (
        <>
          <path d={toothOutline} />
          <path d="m8 4-1-3 3 1.5L12 1l2 1.5L17 1l-1 3M9.5 11.5q2.5 2.5 5 0" />
          <circle cx="9" cy="9" r=".7" fill="currentColor" stroke="none" />
          <circle cx="15" cy="9" r=".7" fill="currentColor" stroke="none" />
        </>
      );
      break;
    case 'second-opinion':
      content = (
        <>
          <rect x="5" y="5" width="14" height="17" rx="2" fill="currentColor" stroke="none" />
          <rect x="9" y="2" width="6" height="5" rx="1.5" fill="currentColor" stroke="#fbf8f1" />
          <path d="m8 13 3 3 5-6" stroke="#fff" />
        </>
      );
      break;
    case 'online-appointment':
      content = (
        <>
          <rect x="2" y="6" width="13" height="12" rx="1.5" fill="currentColor" stroke="none" />
          <path d="m16 10 6-4v12l-6-4Z" fill="currentColor" stroke="none" />
        </>
      );
      break;
    default:
      content = (
        <>
          <rect x="5" y="6" width="14" height="12" rx="2" />
          <path d="M9 12h6" />
        </>
      );
  }

  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {content}
    </svg>
  );
};

